import { FC } from 'react';
import Skeleton from '@srm/ui/skeleton';
import './DocumentSkeleton.scss';

// Геометрия повторяет типичную view-шапку документа: три базовых действия и
// два title-bar action. Важны не подписи, а те же занимаемые слоты до загрузки.
const ACTION_WIDTHS = [164, 116, 124, 220, 160];
const TAB_WIDTHS = [136, 178, 112, 64, 104, 110, 132];

const FieldSkeleton: FC<{ labelWidth: number; valueWidth: string }> = ({ labelWidth, valueWidth }) => (
    <div className="document-skeleton__field">
        <Skeleton height={10} width={labelWidth} radius="sm" />
        <Skeleton height={15} width={valueWidth} radius="sm" />
    </div>
);

const SectionTitle: FC<{ width: number }> = ({ width }) => (
    <div className="document-skeleton__section-title">
        <Skeleton height={17} width={width} radius="sm" />
    </div>
);

interface IDocumentSkeletonProps {
    /** `body` — только вкладки и содержимое: шапка и действия документа уже на экране,
     *  а видимые вкладки ещё считаются (правила видимости могут ждать внешние источники). */
    variant?: 'page' | 'body';
}

/** Content-shaped placeholder shown while the document structure is loading. */
const DocumentSkeleton: FC<IDocumentSkeletonProps> = ({ variant = 'page' }) => (
    <div
        className={variant === 'body' ? 'document-skeleton document-skeleton--body' : 'document-skeleton'}
        role="status"
        aria-label="Открываем документ"
    >
        <div className="document-skeleton__visual" data-testid="document-skeleton-visual" aria-hidden="true">
            {variant === 'page' && (
                <>
                    <header className="document-skeleton__header">
                        <div className="document-skeleton__title-row">
                            <div className="document-skeleton__title-group">
                                <Skeleton className="document-skeleton__title" height={30} radius="sm" />
                                <Skeleton height={22} width={22} circle />
                                <Skeleton height={22} width={22} circle />
                                <Skeleton className="document-skeleton__status" height={22} radius="xl" />
                            </div>
                            <Skeleton className="document-skeleton__route" height={16} radius="sm" />
                        </div>
                    </header>

                    <div className="document-skeleton__actions">
                        {ACTION_WIDTHS.map((width) => (
                            <Skeleton
                                key={width}
                                className="document-skeleton__action"
                                data-testid="document-skeleton-action"
                                height={32}
                                width={width}
                                radius={8}
                            />
                        ))}
                    </div>
                </>
            )}

            <div className="document-skeleton__tabs">
                {TAB_WIDTHS.map((width, index) => (
                    <div key={width} className="document-skeleton__tab" data-testid="document-skeleton-tab">
                        <Skeleton height={12} width={width} radius="sm" />
                        {index === 0 && <span className="document-skeleton__active-tab" />}
                    </div>
                ))}
            </div>

            <main className="document-skeleton__content">
                <section className="document-skeleton__section">
                    <SectionTitle width={154} />
                    <div className="document-skeleton__fields document-skeleton__fields--three">
                        <FieldSkeleton labelWidth={88} valueWidth="54%" />
                        <FieldSkeleton labelWidth={104} valueWidth="34%" />
                        <FieldSkeleton labelWidth={132} valueWidth="48%" />
                        <FieldSkeleton labelWidth={126} valueWidth="68%" />
                        <FieldSkeleton labelWidth={72} valueWidth="72%" />
                        <FieldSkeleton labelWidth={96} valueWidth="42%" />
                    </div>
                </section>

                <section className="document-skeleton__section">
                    <SectionTitle width={218} />
                    <div className="document-skeleton__person-row">
                        <Skeleton height={28} width={28} circle />
                        <div className="document-skeleton__person-copy">
                            <Skeleton height={9} width={68} radius="sm" />
                            <Skeleton height={14} width={238} radius="sm" />
                        </div>
                    </div>
                </section>

                <section className="document-skeleton__section">
                    <SectionTitle width={176} />
                    <div className="document-skeleton__fields document-skeleton__fields--two">
                        <FieldSkeleton labelWidth={82} valueWidth="42%" />
                        <FieldSkeleton labelWidth={68} valueWidth="32%" />
                        <FieldSkeleton labelWidth={128} valueWidth="64%" />
                        <FieldSkeleton labelWidth={156} valueWidth="56%" />
                    </div>
                </section>

                <section className="document-skeleton__section document-skeleton__section--table">
                    <div className="document-skeleton__table-heading">
                        <Skeleton height={17} width={184} radius="sm" />
                        <Skeleton height={30} width={132} radius={9} />
                    </div>
                    <div className="document-skeleton__table" role="presentation">
                        <div className="document-skeleton__table-row document-skeleton__table-row--header">
                            {[34, 22, 18, 26, 20].map((width) => (
                                <Skeleton key={width} height={9} width={`${width}%`} radius="sm" />
                            ))}
                        </div>
                        {[0, 1, 2].map((row) => (
                            <div key={row} className="document-skeleton__table-row">
                                <Skeleton height={13} width={`${52 - row * 6}%`} radius="sm" />
                                <Skeleton height={13} width={`${34 + row * 5}%`} radius="sm" />
                                <Skeleton height={13} width="48%" radius="sm" />
                                <Skeleton height={13} width={`${64 - row * 7}%`} radius="sm" />
                                <Skeleton height={13} width="38%" radius="sm" />
                            </div>
                        ))}
                    </div>
                </section>
            </main>
        </div>
    </div>
);

export default DocumentSkeleton;
